import React from 'react';
import WizardContainer from '../../components/Wizard/WizardContainer';
import { ApplicationStatus } from '../../types/wizard';
import { usePageTracking, useWizardStepTracking } from '../../hooks/useAnalytics';

interface InterviewScheduledProps {
  applicationStatus: ApplicationStatus;
}

const InterviewScheduled: React.FC<InterviewScheduledProps> = ({ applicationStatus }) => {
  usePageTracking('Interview Scheduled');
  useWizardStepTracking('interview-scheduled', 6, 7);

  // Parse interview date from applicationStatus if available
  const interviewDate = applicationStatus.interviewDate
    ? new Date(applicationStatus.interviewDate)
    : null;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <WizardContainer
      currentStepId="interview-scheduled"
      applicationStatus={applicationStatus}
    >
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 text-center">
          <div className="text-6xl mb-6">📅</div>

          <h1 className="text-3xl font-bold text-charcoal mb-4">
            Interview Scheduled!
          </h1>

          {interviewDate ? (
            <div className="bg-logo-green/10 border-2 border-logo-green rounded-lg p-6 mb-6">
              <p className="text-sm text-gray-600 mb-3">Your interview is scheduled for:</p>
              <div className="text-2xl font-bold text-logo-green mb-2">
                {formatDate(interviewDate)}
              </div>
              <div className="text-xl text-charcoal">
                {formatTime(interviewDate)}
              </div>
            </div>
          ) : (
            <div className="bg-logo-green/10 border-2 border-logo-green rounded-lg p-6 mb-6">
              <p className="text-lg text-charcoal">
                Your interview has been scheduled. Check your email for the date and time!
              </p>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
            <h2 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
              <span>💡</span>
              <span>Interview Tips</span>
            </h2>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Review your application and be ready to discuss your experience</li>
              <li>• Think about your availability and the areas you're comfortable delivering to</li>
              <li>• Prepare questions about the role and working with Valley Eats</li>
              <li>• Make sure you're in a quiet place with good internet connection</li>
              <li>• Join the call a few minutes early to test your audio/video</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>📧 Check your email</strong> for the video call link and calendar invitation. We'll also send you a reminder 24 hours before your interview.
            </p>
          </div>
        </div>
      </div>
    </WizardContainer>
  );
};

export default InterviewScheduled;
